import { ConfirmCardSetupData, loadStripe, PaymentMethodCreateParams, Stripe, StripeCardElement, StripeCardNumberElement } from "@stripe/stripe-js";
import { logger } from "@services/workers/helpers";
import config from "../config";
import { serviceAgreement } from "@utils/objects/index";
import { api, state, ui } from "../../interfaces/interfaces";

export const cardStyle = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Arial, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#32325d",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

export const StripeClient = () => ({
  async getStripe(abort: AbortController) {
    if (!this._stripe) {
      try {
        const response = await api.fetchStripePublicKey(config.debug ? "TEST" : "LIVE", abort);
        if (response) {
          logger.debug(this.namespace, `Publishable KEY fetched ${response}`);
          this._publicKey = response;
        }
      } catch (error) {
        logger.error(this.namespace, "Publishable KEY fetch failed.", error);
        this.setError(error);
      }

      if (!abort.signal.aborted && this._publicKey) {
        this._stripe = await loadStripe(this._publicKey); // load Stripe client into front-end and as property on this object
        return this._stripe;
      }
    }
    return this._stripe;
  },
  async createSetupIntent(body: {
    description: string;
    metadata: {
      [key: string]: string;
    };
  }) {
    this.setProcessing(true);
    this.setInfo("PROCESSING... Attempting to create setup.");
    try {
      const response = await api.fetchStripSetupIntent(body);
      console.log(response);
      if (response && response.clientSecret) {
        logger.debug(this.namespace, "Setup intent completed.", response);
        this._clientSecret = response.clientSecret;
        this._clientSecretId = response.id;
        this.setInfo("PROCESSING... Setup intent completed.");
        console.log(this._clientSecret, this._clientSecretId);
      }
    } catch (error) {
      logger.error(this.namespace, "Setup intent failed.", error);
      // if error is of type object, then we need to stringify it
      if (typeof error === "object") {
        this.setError(JSON.stringify(error));
      } else this.setError(error);
    }
  },
  async getPaymentMethod({ body }: { body: any }) {
    this.setInfo("PROCESSING... Setting card payment");
    try {
      const response = await api.fetchStripePaymentMethod(body);
      if (response && response.id) {
        logger.debug(this.namespace, "Payment method returned.", response);
        this._paymentMethod = response;
        this._last4 = this.paymentMethod.card.last4;
        this.setInfo(`PROCESSING... Card with last 4 digits ${this.last4} set successfully.`);
        // SERVICE AGREEMENT PROCESSING ONLY IF CARD IS SET
        serviceAgreement.serviceAgreementPaymentDetails.stripePaymentMethodId = this.paymentMethod.id;
        serviceAgreement.submitThrottled(ui, state.UserId);
        this.setLast4(this._last4);
      }
    } catch (error) {
      logger.error(this.namespace, "Payment method fetch failed.", error);
      // if error is of type object, then we need to stringify it
      if (typeof error === "object") {
        this.setError(JSON.stringify(error));
      } else this.setError(error);
    }
  },
  async confirmCardSetup() {
    const card = this.card;
    const billing_details = this.billing_details();
    if (!card) throw new Error("No card element found.");
    const data: ConfirmCardSetupData = {
      payment_method: {
        card,
        billing_details,
      },
    };
    const setupIntentResult = await this.stripe?.confirmCardSetup(this._clientSecret, data);
    const { setupIntent, error } = setupIntentResult || {};
    if (setupIntent?.status === "succeeded") {
      logger.debug(this.namespace, "Card setup confirmed.", setupIntent);
      this.setError(null);
      this.setProcessing(false);
      this.setSucceeded(true);
      this._paymentMethodId = setupIntent.payment_method;
      this._setupIntent = setupIntent;
      this._setupIntentId = setupIntent.id;
      await this.getPaymentMethod({ body: { id: this._paymentMethodId } });
    } else {
      logger.error(this.namespace, "Card setup failed.", error);
      this.setError(error?.message);
      this.setProcessing(false);
      this.setSucceeded(false);
    }
  },
  billing_details(): PaymentMethodCreateParams.BillingDetails {
    serviceAgreement.updatePersonalDetails(ui, "Billing", "Contact");
    this.cardholderName = `${ui.CarePlanBillingContactFirstName} ${ui.CarePlanBillingContactLastName}`;
    const {
      addressLine1: rawLine1,
      addressLine2: rawLine2,
      addressLine3: rawLine3,
      city: rawCity,
      postcode: rawPostalCode,
      county: rawState
    } = serviceAgreement.getBillingCustomerData();

    // Convert null values to undefined
    const line1 = rawLine1 ?? undefined;
    const line2 = rawLine2 ?? undefined;
    const line3 = rawLine3 ?? undefined;
    const city = rawCity ?? undefined;
    const postal_code = rawPostalCode ?? undefined;
    const state = rawState ?? undefined;
    const addressLines = [line1 || "", line2 || "", line3 || ""].filter(Boolean);
    if (addressLines.length === 0) throw new Error("No valid address lines found.");
    if (!this.cardholderName.trim() || this.cardholderName.trim().length < 2) throw new Error("Invalid cardholder name.");
    this.phone = `${ui.CarePlanBillingContactLandline ||
      ui.CarePlanBillingContactMobile ||
      ""
      }`;
    this.country = "GB";
    this.email = `${ui.CarePlanBillingContactEmail}`;
    return {
      name: this.cardholderName,
      address: {
        line1,
        line2: line3 ? `${line2}, ${line3}` : line2,
        city,
        country: this.country,
        postal_code,
        state: state,
      },
      email: this.email,
      phone: this.phone,
    };
  },

  namespace: "StripeAPI",
  _stripe: null as Stripe | null,
  _publicKey: null as any,
  _clientSecret: null as any,
  _clientSecretId: null as any,
  _paymentMethodId: null as any,
  _paymentMethod: null as any,
  _setupIntent: null as any,
  _setupIntentId: null as any,
  _last4: null as any,

  _setInfo: null as any,
  get setInfo() {
    return this._setInfo;
  },
  set setInfo(setFunc: any) {
    this._setInfo = setFunc;
  },

  _setLast4: null as any,
  get setLast4() {
    return this._setLast4;
  },
  set setLast4(setFunc: any) {
    this._setLast4 = setFunc;
  },

  _setError: null,
  get setError() {
    return this._setError;
  },
  set setError(setFunc: any) {
    this._setError = setFunc;
  },

  _setProcessing: null,
  get setProcessing() {
    return this._setProcessing;
  },
  set setProcessing(setFunc: any) {
    this._setProcessing = setFunc;
  },

  _setSucceeded: null,
  get setSucceeded() {
    return this._setSucceeded;
  },
  set setSucceeded(setFunc: any) {
    this._setSucceeded = setFunc;
  },

  _card: null as StripeCardElement | StripeCardNumberElement | null,
  get card() {
    return this._card;
  },
  set card(card: StripeCardElement | StripeCardNumberElement | null) {
    this._card = card;
  },

  get stripe() {
    return this._stripe;
  }, // Official Stripe client
  get clientSecretId() {
    return this._clientSecretId;
  },
  get clientSecret() {
    return this._clientSecret;
  },
  get paymentMethodId() {
    return this._paymentMethodId;
  },
  get paymentMethod() {
    return this._paymentMethod;
  },
  get setupIntent() {
    return this._setupIntent;
  },
  get setupIntentId() {
    return this._setupIntentId;
  },
  get last4() {
    return this._last4;
  },

  cardholderName: "",
  country: "",
  email: "",
  state: "",
  phone: "",
});
